import type { ListingAccount, Pagination, SignMsgFn } from "./types";
import { createEditListingInstruction } from "./generated/instructions/editListing";
import { Listing, ListingV1, listingDiscriminator } from "./generated/accounts";
import { EditPayload as EditPayloadArgs } from "./generated/types";
import {
  Connection,
  PublicKey,
  Cluster,
  Transaction,
  AccountInfo,
  clusterApiUrl,
  SendOptions,
  SignaturePubkeyPair
} from "@solana/web3.js";
import Bundlr from "@bundlr-network/client/build/web";
import {
  PROGRAM_ID,
  CURRENT_DATA_VERSION,
} from "./constants";
import bs58 from "bs58";
import {
  EditListingParams,
  ListingFilters,
  ClientInitListingParams,
  ClientEditListingParams
} from "./types";
import { applyFilter, getPage } from "./utils";
import { createInitListingTx, getDataSizeOfListing } from "./listing";
import nacl from "tweetnacl";
import { log } from "./logger";
import { getBundlrNetworkApi, topUpAccOnBundlrNet } from "./upload";

export * from "./types";
export * from "./errors";
export { uploadFileToArweaveViaBundlr, } from "./upload";
export { createInitListingTx } from "./listing";

/**
 * ListingClient handles interaction with solist contract
 */
export class ListingClient {

  private cluster: Cluster;
  private solanaConnection: Connection;
  private publisher: PublicKey;
  private publisherSignMsgFn: SignMsgFn;
  private bundlr: Bundlr

  /**
   * Creates a ListingClient instance.
   * @param cluster "devnet" | "testnet" | "mainnet-beta"
   * @param publisher Public Key of Publisher
   * @param publisherSignMsgFn A function that signs message with Publisher
   */
  constructor(cluster: Cluster, publisher: PublicKey, publisherSignMsgFn: SignMsgFn) {
    const clusterApi = clusterApiUrl(cluster);
    this.cluster = cluster;
    this.solanaConnection = new Connection(clusterApi);
    this.publisher = publisher;
    this.publisherSignMsgFn = publisherSignMsgFn;
    this.bundlr = this.createPublisherBundlrInstance(cluster, publisher, publisherSignMsgFn);
  }

  private createPublisherBundlrInstance(cluster: Cluster, publisher: PublicKey, signMsgFn: SignMsgFn): Bundlr {
    const provider = {
      publicKey: {
        toBuffer: () => publisher.toBuffer(),
        byteLength: 32
      },
      signMessage: async (message: Uint8Array): Promise<Uint8Array> => {
        const signature = await signMsgFn(message);
        return signature;
      },
      sendTransaction: async (tx: Transaction, c: Connection, opts: SendOptions): Promise<string> => {
        const connection = new Connection(clusterApiUrl(cluster));
        if (tx.signatures.filter(txSignature => txSignature.signature === null)) {
          const msgBuffer = tx.serializeMessage();
          const signature = await signMsgFn(msgBuffer);

          /// Inject signature
          let signaturePubKeyPair = tx.signatures.pop() as SignaturePubkeyPair;
          signaturePubKeyPair.signature = Buffer.from(signature);
          tx.signatures = [signaturePubKeyPair];
        }
        const serializedTx = tx.serialize({
          verifySignatures: true,
          requireAllSignatures: true
        });
        const txId = await connection.sendRawTransaction(serializedTx, opts);
        return txId;
      },
    };

    return new Bundlr(
      getBundlrNetworkApi(cluster),
      "solana",
      provider,
      {
        providerUrl: clusterApiUrl(cluster),
      }
    );
  }

  /**
   * Creates a Listing:
   * 
   * - Uploads asset to Bundlr Network if necessary. If it's passed as `content`.
   * - Creates and submit Listing account to Solana network
   */
  public async initListing(params: ClientInitListingParams): Promise<string> {
    const {
      listingAcc,
      application,
      header,
      content,
      expirationDays,
      assets,
      tags
    } = params;

    // Upload assets to bundlr
    const arweaveTxIds: string[] = [];
    for (let asset of assets) {
      if (asset.content !== undefined) {
        log.info("Preparing asset for Bundlr upload");
        const arweaveTxId = await this.uploadContentToArweaveViaBundlr(asset.content);
        arweaveTxIds.push(arweaveTxId);
      } else if (asset.arweaveTxId !== undefined) {
        arweaveTxIds.push(asset.arweaveTxId);
      }
    }

    // Handles initialized Listing
    const tx = await createInitListingTx(
      this.solanaConnection,
      this.cluster,
      this.publisher,
      {
        listingAcc,
        application,
        header,
        content,
        expirationDays,
        assets: arweaveTxIds,
        tags
      }
    );

    // Manipulate signatures within transaction object 
    // Signatures must be [publisherSignature, listingAccSignature, publisherSignature]
    const message = tx.serializeMessage();
    const publisherSignature = await this.publisherSignMsgFn(message);
    const listingSignature = await nacl.sign.detached(message, listingAcc.secretKey);

    tx.signatures = [];
    tx.addSignature(this.publisher, Buffer.from(publisherSignature));
    tx.addSignature(listingAcc.publicKey, Buffer.from(listingSignature))
    tx.addSignature(this.publisher, Buffer.from(publisherSignature));

    // Send tx to blockchain
    const serializedTx = tx.serialize({
      verifySignatures: true,
      requireAllSignatures: true
    });
    const signature = await this.solanaConnection.sendRawTransaction(serializedTx, {
      skipPreflight: true
    });
    log.info(`Submitted transaction with signature ${signature}`);
    return signature;
  }

  /**
   * Uploads base64 encoded image string to Arweave via Bundlr network.
   */
  public async uploadContentToArweaveViaBundlr(content: string): Promise<string> {
    const price = await this.bundlr.getPrice(content.length);
    log.info(`Content requires about ${price}`);

    // Lazy top up of account in Bundlr node if lacking funds
    await topUpAccOnBundlrNet(this.solanaConnection, this.bundlr, this.publisher, price.toNumber());

    // Create, sign and upload transaction
    const tx = this.bundlr.createTransaction(content);
    await tx.sign();
    log.info(`Uploading ${tx.id} to BundlrNetwork...`);
    await tx.upload();
    log.info(`Uploaded ${tx.id} to BundlrNetwork...`);
    return tx.id;
  }

  /**
   * Estimate the cost of init listing operation in lamports.
   * 
   * This includes:
   *   - Price of uploading assets to Arweave via Bundlr 
   *   - Storage cost of Listing account
   */
  public async estimateCostOfInitListing(params: ClientInitListingParams): Promise<number> {
    const {
      assets,
    } = params;

    let totalLamports = 0;

    // Estimate cost of assets upload
    const assetsToUpload = assets.filter(asset => asset.content !== undefined).map(asset => asset.content);
    for (let content of assetsToUpload) {
      const price = await this.bundlr.getPrice((content as string).length);
      totalLamports += price.toNumber();
    }

    // Estimate cost of Listing
    const dataSize = getDataSizeOfListing();
    const storageRent = await this.solanaConnection.getMinimumBalanceForRentExemption(dataSize);
    totalLamports += storageRent;

    return totalLamports;
  }


  /**
   * Edit a listing:
   *  - Uploads asset to Bundlr Network if necessary. If it's passed as `content`.
   *  - Creates and submit transaction to edit Listing account to Solana network 
   */
  public async editListing(listingAcc: PublicKey, params: ClientEditListingParams): Promise<string> {
    const payload: EditPayloadArgs = {
      header: params.header || null,
      content: params.content || null,
      expiringAt: params.expiringAt ? Number(params.expiringAt) : null,
      tags: params.tags ? params.tags : null,
      assets: null,
    };

    // Handle upload of asset to Bundlr Network
    if (params.assets) {
      let arweaveTxIds: string[] = [];
      for (const asset of params.assets) {
        if (asset.content !== undefined) {
          log.info("Preparing asset for Bundlr upload");
          const arweaveTxId = await this.uploadContentToArweaveViaBundlr(asset.content);
          arweaveTxIds.push(arweaveTxId);
        } else if (asset.arweaveTxId !== undefined) {
          arweaveTxIds.push(asset.arweaveTxId);
        }
      }
      payload.assets = arweaveTxIds;
    }

    // Build transaction
    const ix = createEditListingInstruction(
      {
        listing: listingAcc,
        signer: this.publisher,
      },
      {
        payload
      }
    );
    const {
      blockhash, lastValidBlockHeight
    } = await this.solanaConnection.getLatestBlockhash();
    const tx = new Transaction();
    tx.recentBlockhash = blockhash;
    tx.lastValidBlockHeight = lastValidBlockHeight;
    tx.add(ix);
    tx.feePayer = this.publisher;

    const message = tx.serializeMessage();
    const publisherSignature = await this.publisherSignMsgFn(message);
    tx.addSignature(this.publisher, Buffer.from(publisherSignature));

    // Send tx to the blockchain
    const serializedTx = tx.serialize({
      verifySignatures: true,
      requireAllSignatures: true
    });
    const signature = await this.solanaConnection.sendRawTransaction(serializedTx, {
      skipPreflight: true
    });
    log.info(`Submitted transaction with signature ${signature}`);
    return signature;
  }

  /**
   * Fetch single listing given public key.
   * @param connection An RPC connection
   * @param pubkey Listing account to be fetched.
   * @returns 
   */
  public static async fetchSingleListing(connection: Connection, pubkey: PublicKey): Promise<ListingAccount> {
    const retrieved = await connection.getAccountInfo(pubkey);
    if (retrieved === null) {
      throw new Error(`No Listing account found at ${pubkey}`);
    }
    return buildListingAccountFromAccountData(retrieved, pubkey);
  }

  /**
   * Fetch all Listing accounts from program.
   *
   * Sorted in descending order of `createdAt`.
   * in one call.
   *
   * @param filters Filters to be applied
   * @param pagination Pagination settings. Default to { page: 1 , perPage: 20 }
   *
   * @example
   * // Filter 20 most recent listings which contain any of these tags "sample1" "sample2"
   * await fetchAllListingAccs(connection, { page: 1, perPage: 20 }, { tags: ["sample1", "sample2"] });
   *
   * @example
   * // Filter 20 most recent listings created one hour ago
   * const start = Math.floor(new Date().getTime() / 1000) - 60 * 60;
   * await fetchAllListingAccs(connection, { page: 1, perPage: 20 } , { createdAt: { start } });
   *
   * @example
   * // Filter next 20 recent listings.
   * await fetchAllListingAccs(connection, { page: 2, perPage: 20 });
   *
   */
  public static async fetchAllListings(connection: Connection, pagination?: Pagination, filters?: ListingFilters): Promise<ListingAccount[]> {
    return await fetchAllListingAccs(connection, pagination, filters);
  }

}

const getDiscriminator = (buffer: Buffer): number[] => {
  const discriminator = [];
  for (let i = 0; i < 8; i += 1) {
    discriminator.push(buffer.readUInt8(i));
  }
  return discriminator;
};

const buildListingAccountFromAccountData = (
  listingAccountData: AccountInfo<Buffer>,
  pubkey: PublicKey
): ListingAccount => {
  const discriminator = getDiscriminator(listingAccountData.data);

  // Listing V0
  if (JSON.stringify(discriminator) === JSON.stringify(listingDiscriminator)) {
    const [listingV0Res, _] = Listing.fromAccountInfo(listingAccountData);
    return {
      header: listingV0Res.header,
      content: listingV0Res.content,
      id: pubkey.toBase58(),
      publisher: listingV0Res.publisher.toBase58(),
      application: listingV0Res.application.toBase58(),
      createdAt: Number(listingV0Res.createdAt),
      expiringAt: Number(listingV0Res.expiringAt),
      active: true,
      updatedAt: Number(listingV0Res.updatedAt),
      assets: listingV0Res.assets,
      tags: listingV0Res.tags,
      history: listingV0Res.history.map((snapshot) => ({
        header: snapshot.header || null,
        content: snapshot.content || null,
        expiringAt: snapshot.expiringAt ? Number(snapshot.expiringAt) : null,
        assets: snapshot.assets || null,
        updatedAt: Number(snapshot.updatedAt),
      })),
    };
  }
  // Handles latest version of Listing
  const [listingRes, _] = ListingV1.fromAccountInfo(listingAccountData);
  return {
    header: listingRes.header,
    content: listingRes.content,
    id: pubkey.toBase58(),
    publisher: listingRes.publisher.toBase58(),
    application: listingRes.application.toBase58(),
    createdAt: Number(listingRes.createdAt),
    expiringAt: listingRes.expiringAt
      ? Number(listingRes.expiringAt)
      : undefined,
    active: listingRes.active,
    updatedAt: Number(listingRes.updatedAt),
    assets: listingRes.assets,
    tags: listingRes.tags,
    history: listingRes.history.map((snapshot) => ({
      header: snapshot.header || null,
      content: snapshot.content || null,
      expiringAt: snapshot.expiringAt ? Number(snapshot.expiringAt) : null,
      assets: snapshot.assets || null,
      updatedAt: Number(snapshot.updatedAt),
    })),
  };
};

/**
 * Fetch all Listing accounts from program.
 *
 * Sorted in descending order of `createdAt`.
 * in one call.
 *
 * @param connection Connection to Solana fullnode
 * @param filters Filters to be applied
 * @param pagination Pagination settings. Default to { page: 1 , perPage: 20 }
 *
 * @example
 * // Filter 20 most recent listings which contain any of these tags "sample1" "sample2"
 * await fetchAllListingAccs(connection, { page: 1, perPage: 20 }, { tags: ["sample1", "sample2"] });
 *
 * @example
 * // Filter 20 most recent listings created one hour ago
 * const start = Math.floor(new Date().getTime() / 1000) - 60 * 60;
 * await fetchAllListingAccs(connection, { page: 1, perPage: 20 } , { createdAt: { start } });
 *
 * @example
 * // Filter next 20 recent listings.
 * await fetchAllListingAccs(connection, { page: 2, perPage: 20 });
 *
 *  @deprecated Use ListingClient.fetchAllListings() instead
 */
export const fetchAllListingAccs = async (
  connection: Connection,
  pagination?: Pagination,
  filters?: ListingFilters
): Promise<ListingAccount[]> => {
  // TODO: Cannot use `dataSlice` because some of the accounts do not
  // conform to the latest struct
  const accounts = await connection.getProgramAccounts(PROGRAM_ID, {
    commitment: "processed",
    filters: [
      {
        memcmp: {
          offset: 8, // Candy Machine Discriminator
          bytes: bs58.encode([CURRENT_DATA_VERSION]),
        },
      },
    ],
  });
  let listingAccs = accounts.map((account) =>
    buildListingAccountFromAccountData(account.account, account.pubkey)
  );
  // Apply filters
  if (filters !== undefined) {
    listingAccs = applyFilter(listingAccs, filters);
  }
  return getPage(listingAccs, pagination);
};

/**
 * Fetch a single listing
 * @deprecated Use ListingClient.fetchSingleListing() instead
 */
export const fetchSingleListing = async (
  connection: Connection,
  pubkey: PublicKey
): Promise<ListingAccount> => {
  const retrieved = await connection.getAccountInfo(pubkey);
  if (retrieved === null) {
    throw new Error(`No Listing account found at ${pubkey}`);
  }
  return buildListingAccountFromAccountData(retrieved, pubkey);
};

/**
 * Edit listing of pubkey
 * @deprecated Use ListingClient.editListing() instead
 */
export const createEditListingTx = async (
  connection: Connection,
  payload: EditListingParams
): Promise<Transaction> => {
  const ix = createEditListingInstruction(
    {
      listing: payload.listingAcc,
      signer: payload.publisher,
    },
    {
      payload: {
        header: payload.header || null,
        content: payload.content || null,
        expiringAt: payload.expiringAt ? Number(payload.expiringAt) : null,
        tags: payload.tags ? payload.tags : null,
        assets: payload.assets ? payload.assets : null,
      },
    }
  );
  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash();
  const tx = new Transaction();
  tx.add(ix);
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;
  tx.feePayer = payload.publisher;
  return tx;
};
